import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  List,
  ListItem,
  ListItemSecondaryAction,
  Divider,
  Paper,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Article as ArticleIcon,
  OpenInNew as OpenInNewIcon,
  Mic as MicIcon,
  Audiotrack as AudiotrackIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  VolumeUp as VolumeUpIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { apiService, ShowSegment, NewsItem, NewsArticle, ExternalNewsArticle } from '../services/api';
import Breadcrumb from './Breadcrumb';

const SegmentManagement: React.FC = () => {
  const navigate = useNavigate();
  const { segmentId, episodeId } = useParams<{ segmentId: string; episodeId: string }>();
  const [segment, setSegment] = useState<ShowSegment | null>(null);
  const [episode, setEpisode] = useState<any>(null);
  const [show, setShow] = useState<any>(null);
  const [channel, setChannel] = useState<any>(null);
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddNewsDialog, setShowAddNewsDialog] = useState(false);
  const [showScriptGenerationDialog, setShowScriptGenerationDialog] = useState(false);
  const [showEditSegmentDialog, setShowEditSegmentDialog] = useState(false);
  const [editSegmentData, setEditSegmentData] = useState({
    title: '',
    description: '',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<NewsArticle[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [originalExternalArticles, setOriginalExternalArticles] = useState<ExternalNewsArticle[]>([]);
  const [searchFilters, setSearchFilters] = useState({
    language: 'en',
    categories: '',
    publishedAfter: '',
    publishedBefore: '',
    limit: 20,
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<'openai' | 'google'>('openai');
  const [scriptPrompt, setScriptPrompt] = useState('');
  const [scriptStyle, setScriptStyle] = useState('conversational');
  const [expandedNewsItems, setExpandedNewsItems] = useState<Set<string>>(new Set());
  
  // Operation loading states
  const [operationStates, setOperationStates] = useState({
    addingNewsItem: false,
    generatingScript: false,
    generatingAudio: false,
    deletingNewsItem: false,
    reorderingNewsItems: false,
  });
  
  // Refs to store polling timeouts for cleanup
  const pollingTimeoutsRef = useRef<{ [key: string]: NodeJS.Timeout }>({});
  
  // Audio playback state
  const [audioState, setAudioState] = useState<{
    isPlaying: boolean;
    currentSegmentId: string | null;
    currentPartIndex: number | null;
    audioElement: HTMLAudioElement | null;
  }>({
    isPlaying: false,
    currentSegmentId: null,
    currentPartIndex: null,
    audioElement: null,
  });

  useEffect(() => {
    if (segmentId && episodeId) {
      fetchSegmentData();
    }
  }, [segmentId, episodeId]);

  const fetchSegmentData = async () => {
    if (!segmentId || !episodeId) return;
    
    try {
      setLoading(true);
      const [segmentData, newsItemsData, episodeData] = await Promise.all([
        apiService.getShowSegment(segmentId),
        apiService.getSegmentNewsItems(segmentId),
        apiService.getEpisode(episodeId),
      ]);
      setSegment(segmentData);
      setNewsItems(newsItemsData);
      setEpisode(episodeData);
      
      // Fetch show and channel data for breadcrumbs
      if (episodeData?.showId) {
        const showData = await apiService.getShow(episodeData.showId);
        setShow(showData);
        
        if (showData?.channelId) {
          const channelData = await apiService.getChannel(showData.channelId);
          setChannel(channelData);
        }
      }
    } catch (error) {
      console.error('Error fetching segment data:', error);
      setError('Failed to fetch segment data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNewsItem = async (newsItemId: string) => {
    if (window.confirm('Are you sure you want to remove this news item from the segment?')) {
      try {
        setOperationStates(prev => ({ ...prev, deletingNewsItem: true }));
        await apiService.deleteNewsItemFromSegment(segmentId!, newsItemId);
        setSuccessMessage('News item removed successfully');
        setShowSuccessMessage(true);
        fetchSegmentData(); // Refresh the data
      } catch (error) {
        console.error('Error deleting news item:', error);
        setError('Failed to remove news item');
      } finally {
        setOperationStates(prev => ({ ...prev, deletingNewsItem: false }));
      }
    }
  };

  const handleDragStart = (e: React.DragEvent, newsItemId: string) => {
    setDraggedItem(newsItemId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetNewsItemId: string) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem === targetNewsItemId) {
      setDraggedItem(null);
      return;
    }

    const draggedIndex = newsItems.findIndex(item => item._id === draggedItem);
    const targetIndex = newsItems.findIndex(item => item._id === targetNewsItemId);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedItem(null);
      return;
    }

    const items = Array.from(newsItems);
    const [reorderedItem] = items.splice(draggedIndex, 1);
    items.splice(targetIndex, 0, reorderedItem);

    setNewsItems(items);
    setDraggedItem(null);

    try {
      setOperationStates(prev => ({ ...prev, reorderingNewsItems: true }));
      const newsItemIds = items.map(item => item._id);
      await apiService.reorderNewsItemsInSegment(segmentId!, newsItemIds);
      setSuccessMessage('News items reordered successfully');
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Error reordering news items:', error);
      setError('Failed to reorder news items');
      // Revert the order if the API call failed
      fetchSegmentData();
    } finally {
      setOperationStates(prev => ({ ...prev, reorderingNewsItems: false }));
    }
  };

  const handleSearchNews = async () => {
    if (!searchQuery.trim()) return;

    try {
      setSearchLoading(true);
      setError('');
      
      // Use the external news search API with all supported criteria
      const searchParams = {
        query: searchQuery.trim(),
        language: searchFilters.language,
        limit: searchFilters.limit,
        ...(searchFilters.categories && { category: searchFilters.categories }),
        ...(searchFilters.publishedAfter && { published_after: searchFilters.publishedAfter }),
        ...(searchFilters.publishedBefore && { published_before: searchFilters.publishedBefore }),
      };
      
      const results = await apiService.searchExternalNews(searchParams);
      // Store original external articles for ingestion
      setOriginalExternalArticles(results);
      
      // Convert ExternalNewsArticle to NewsArticle format for display
      const convertedResults = results.map(article => ({
        _id: article.uuid,
        title: article.title,
        description: article.description,
        content: article.content,
        fullContents: article.content,
        url: article.url,
        urlToImage: article.image_url || '',
        publishedAt: article.published_at,
        source: { 
          id: article.source_id || article.source,
          name: article.source 
        },
        category: article.categories?.[0] || 'general',
        language: article.language,
        contentExtractedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }));
      setSearchResults(convertedResults);
      
      if (results.length === 0) {
        console.log('No external news found, trying internal news search...');
        // Fallback to internal news search if no external results
        const internalResults = await apiService.searchNews(searchQuery.trim(), 20);
        setSearchResults(internalResults);
      }
    } catch (error) {
      console.error('Error searching news:', error);
      setError('Failed to search news. Please try again.');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddNewsToSegment = async (article: NewsArticle) => {
    try {
      setOperationStates(prev => ({ ...prev, addingNewsItem: true }));
      
      // Check if this is an external article by looking for it in the original external articles
      const originalExternalArticle = originalExternalArticles.find(ext => ext.uuid === article._id);
      
      if (originalExternalArticle) {
        // This is an external article, we need to ingest it first
        console.log('Ingesting external article:', article._id);
        
        // Ingest the external article into our database
        const ingestResponse = await apiService.ingestExternalArticle(article._id, originalExternalArticle);
        
        console.log('Ingestion response:', ingestResponse);
        
        // Use the ingested article's ID
        const ingestedArticleId = ingestResponse.article_id;
        
        // Add the ingested article to the segment
        await apiService.addNewsItemToSegment(segmentId!, {
          newsArticleId: ingestedArticleId,
          sourceMaterials: [],
        });
        
        setSuccessMessage('External news article ingested and added to segment successfully');
      } else {
        // This is an internal article, add it directly
        await apiService.addNewsItemToSegment(segmentId!, {
          newsArticleId: article._id,
          sourceMaterials: [],
        });
        setSuccessMessage('News item added to segment successfully');
      }
      
      setShowAddNewsDialog(false);
      setShowSuccessMessage(true);
      fetchSegmentData(); // Refresh the data
    } catch (error) {
      console.error('Error adding news to segment:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      setError(`Failed to add news to segment: ${errorMessage}`);
    } finally {
      setOperationStates(prev => ({ ...prev, addingNewsItem: false }));
    }
  };

  const handleCloseAddNewsDialog = () => {
    setShowAddNewsDialog(false);
    // Clear search state when closing the modal
    setSearchQuery('');
    setSearchResults([]);
    setOriginalExternalArticles([]);
    setError('');
    setSearchLoading(false);
    setShowAdvancedFilters(false);
    setSearchFilters({
      language: 'en',
      categories: '',
      publishedAfter: '',
      publishedBefore: '',
      limit: 20,
    });
  };

  const handleGenerateScript = () => {
    setShowScriptGenerationDialog(true);
  };

  const handleOpenEditSegmentDialog = () => {
    if (segment) {
      setEditSegmentData({
        title: segment.title,
        description: segment.description || '',
      });
      setShowEditSegmentDialog(true);
    }
  };

  const handleCloseEditSegmentDialog = () => {
    setShowEditSegmentDialog(false);
    setEditSegmentData({
      title: '',
      description: '',
    });
  };

  const handleUpdateSegment = async () => {
    if (!segment || !editSegmentData.title.trim()) {
      setError('Segment title is required');
      return;
    }

    try {
      setError('');
      await apiService.updateShowSegment(segment._id, {
        title: editSegmentData.title.trim(),
        description: editSegmentData.description.trim(),
      });
      
      // Update local state
      setSegment({
        ...segment,
        title: editSegmentData.title.trim(),
        description: editSegmentData.description.trim(),
      });
      
      handleCloseEditSegmentDialog();
      setSuccessMessage('Segment updated successfully');
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Error updating segment:', error);
      setError('Failed to update segment');
    }
  };

  const handleScriptGenerationSubmit = async () => {
    if (!segmentId || !episodeId) return;
    
    try {
      setOperationStates(prev => ({ ...prev, generatingScript: true }));
      setError(''); // Clear any previous errors
      setShowScriptGenerationDialog(false);
      setSuccessMessage('Script generation started...');
      setShowSuccessMessage(true);
      
      await apiService.generateScript(segmentId, {
        showSegmentId: segmentId,
        episodeId: episodeId,
        requestedBy: 'current-user',
        provider: selectedProvider,
        prompt: scriptPrompt,
        style: scriptStyle,
      });
      
      setSuccessMessage(`Script generation request submitted successfully using ${selectedProvider === 'openai' ? 'OpenAI' : 'Google (Gemini)'}. This may take 30-60 seconds...`);
      setShowSuccessMessage(true);
      
      // Keep the loading state active and poll for completion
      const pollForScriptCompletion = async () => {
        try {
          const updatedSegment = await apiService.getShowSegment(segmentId);
          
          // Check if script generation is complete (has script or scriptParts)
          if (updatedSegment.script || (updatedSegment.scriptParts && updatedSegment.scriptParts.length > 0)) {
            setOperationStates(prev => ({ ...prev, generatingScript: false }));
            setSuccessMessage('Script generation completed successfully!');
            setShowSuccessMessage(true);
            fetchSegmentData(); // Refresh to show final state
            return;
          }
          
          // If no script yet, poll again in 3 seconds
          pollingTimeoutsRef.current.script = setTimeout(pollForScriptCompletion, 3000);
          
        } catch (pollError) {
          console.error('Error polling for script completion:', pollError);
          // Stop polling on error after 2 minutes
          pollingTimeoutsRef.current.script = setTimeout(() => {
            setOperationStates(prev => ({ ...prev, generatingScript: false }));
            setSuccessMessage('Script generation is in progress. Check back later.');
            setShowSuccessMessage(true);
          }, 120000); // 2 minutes timeout
        }
      };
      
      // Start polling after a short delay
      pollingTimeoutsRef.current.script = setTimeout(pollForScriptCompletion, 2000);
      
    } catch (error) {
      console.error('Error generating script:', error);
      setError('Failed to generate script');
      setOperationStates(prev => ({ ...prev, generatingScript: false }));
    }
  };

  const handleGenerateAudio = async () => {
    if (!segmentId) return;
    
    try {
      setOperationStates(prev => ({ ...prev, generatingAudio: true }));
      setError(''); // Clear any previous errors
      setSuccessMessage('Audio generation started...');
      setShowSuccessMessage(true);
      
      await apiService.requestAudioGeneration(segmentId);
      
      setSuccessMessage('Audio generation request submitted successfully. This may take several minutes...');
      setShowSuccessMessage(true);
      
      // Keep the loading state active and poll for completion
      const pollForCompletion = async () => {
        try {
          const updatedSegment = await apiService.getShowSegment(segmentId);
          
          // Check if audio generation is complete
          if (updatedSegment.audioGenerationStatus === 'completed' || 
              updatedSegment.audioGenerationStatus === 'failed') {
            setOperationStates(prev => ({ ...prev, generatingAudio: false }));
            setSuccessMessage(`Audio generation ${updatedSegment.audioGenerationStatus}!`);
            setShowSuccessMessage(true);
            fetchSegmentData(); // Refresh to show final state
            return;
          }
          
          // If still generating, poll again in 5 seconds
          if (updatedSegment.audioGenerationStatus === 'generating') {
            pollingTimeoutsRef.current.audio = setTimeout(pollForCompletion, 5000);
          } else {
            // If status is unknown or other, stop polling after 2 minutes
            pollingTimeoutsRef.current.audio = setTimeout(() => {
              setOperationStates(prev => ({ ...prev, generatingAudio: false }));
              setSuccessMessage('Audio generation is in progress. Check back later.');
              setShowSuccessMessage(true);
            }, 120000); // 2 minutes timeout
          }
        } catch (pollError) {
          console.error('Error polling for audio completion:', pollError);
          // Stop polling on error
          setOperationStates(prev => ({ ...prev, generatingAudio: false }));
        }
      };
      
      // Start polling after a short delay
      pollingTimeoutsRef.current.audio = setTimeout(pollForCompletion, 3000);
      
    } catch (error) {
      console.error('Error generating audio:', error);
      setError('Failed to generate audio');
      setOperationStates(prev => ({ ...prev, generatingAudio: false }));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'error';
      case 'draft': return 'warning';
      case 'archived': return 'default';
      case 'published': return 'info';
      default: return 'default';
    }
  };

  // Audio playback functions
  const handlePlayAudioForSegment = async (segment: ShowSegment, partIndex?: number) => {
    if (!segment) return;

    try {
      const freshSegment = await apiService.getShowSegment(segment._id);
      setSegment(freshSegment);

      const freshScriptPartsWithAudio = freshSegment.scriptParts.filter((part: any) => part.audioUrl);
      if (freshScriptPartsWithAudio.length === 0) {
        setError("No audio parts available for playback.");
        return;
      }

      if (audioState.isPlaying && audioState.currentSegmentId === segment._id && (partIndex === undefined || audioState.currentPartIndex === partIndex)) {
        // If already playing this segment/part, stop it
        if (audioState.audioElement) {
          audioState.audioElement.pause();
          audioState.audioElement.currentTime = 0;
        }
        setAudioState({ isPlaying: false, currentSegmentId: null, currentPartIndex: null, audioElement: null });
        return;
      }

      // Stop any currently playing audio
      if (audioState.audioElement) {
        audioState.audioElement.pause();
        audioState.audioElement.currentTime = 0;
      }

      const playPart = async (index: number) => {
        if (index >= freshScriptPartsWithAudio.length) {
          // All parts played
          setAudioState({ isPlaying: false, currentSegmentId: null, currentPartIndex: null, audioElement: null });
          return;
        }

        const partToPlay = freshScriptPartsWithAudio[index];
        
        // Get audio as blob with authentication
        const audioBlob = await apiService.getAudioFile(segment._id, partToPlay.order);
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);

        audio.onplay = () => {
          setAudioState({ isPlaying: true, currentSegmentId: segment._id, currentPartIndex: partToPlay.order, audioElement: audio });
        };

        audio.onended = () => {
          // Clean up object URL to prevent memory leaks
          URL.revokeObjectURL(audioUrl);
          
          if (partIndex === undefined) { // If playing all parts sequentially
            playPart(index + 1);
          } else { // If playing a single part
            setAudioState({ isPlaying: false, currentSegmentId: null, currentPartIndex: null, audioElement: null });
          }
        };

        audio.onerror = (e) => {
          console.error("Audio playback error:", e);
          // Clean up object URL to prevent memory leaks
          URL.revokeObjectURL(audioUrl);
          setAudioState({ isPlaying: false, currentSegmentId: null, currentPartIndex: null, audioElement: null });
          setError(`Failed to play audio for part ${partToPlay.order + 1}.`);
        };

        audio.play();
      };

      if (partIndex !== undefined) {
        // Play a specific part
        const targetPart = freshScriptPartsWithAudio.find((p: any) => p.order === partIndex);
        if (targetPart) {
          playPart(freshScriptPartsWithAudio.indexOf(targetPart));
        } else {
          setError(`Audio part ${partIndex + 1} not found.`);
        }
      } else {
        // Play all parts sequentially
        playPart(0);
      }

    } catch (error: any) {
      console.error("Error during audio playback:", error);
      setError(`Audio playback failed: ${error.message}`);
      setAudioState({ isPlaying: false, currentSegmentId: null, currentPartIndex: null, audioElement: null });
    }
  };

  const handleStopAudio = () => {
    if (audioState.audioElement) {
      audioState.audioElement.pause();
      audioState.audioElement.currentTime = 0;
    }
    setAudioState({ isPlaying: false, currentSegmentId: null, currentPartIndex: null, audioElement: null });
  };

  const toggleNewsItemExpansion = (newsItemId: string) => {
    setExpandedNewsItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(newsItemId)) {
        newSet.delete(newsItemId);
      } else {
        newSet.add(newsItemId);
      }
      return newSet;
    });
  };

  // Cleanup audio and polling timeouts on unmount
  useEffect(() => {
    return () => {
      if (audioState.audioElement) {
        audioState.audioElement.pause();
        audioState.audioElement.currentTime = 0;
      }
      // Clear all polling timeouts
      Object.values(pollingTimeoutsRef.current).forEach(timeout => {
        clearTimeout(timeout);
      });
    };
  }, [audioState.audioElement]);

  if (loading) {
    return (
      <Box>
        <Breadcrumb items={[
          { label: 'Channels', path: '/channels', icon: <ArticleIcon /> },
          { label: channel?.name || 'Channel', path: channel?._id ? `/channels/${channel._id}` : '/channels', icon: <ArticleIcon /> },
          { label: show?.name || 'Show', path: show?._id ? `/shows/${show._id}` : '/channels', icon: <ArticleIcon /> },
          { label: episode?.title || 'Episode', path: `/episodes/${episodeId}`, icon: <ArticleIcon /> },
          { label: 'Segment Management', path: `/episodes/${episodeId}/segments/${segmentId}`, icon: <ArticleIcon /> }
        ]} />
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Breadcrumb items={[
          { label: 'Channels', path: '/channels', icon: <ArticleIcon /> },
          { label: channel?.name || 'Channel', path: channel?._id ? `/channels/${channel._id}` : '/channels', icon: <ArticleIcon /> },
          { label: show?.name || 'Show', path: show?._id ? `/shows/${show._id}` : '/channels', icon: <ArticleIcon /> },
          { label: episode?.title || 'Episode', path: `/episodes/${episodeId}`, icon: <ArticleIcon /> },
          { label: 'Segment Management', path: `/episodes/${episodeId}/segments/${segmentId}`, icon: <ArticleIcon /> }
        ]} />
        <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
      </Box>
    );
  }

  if (!segment) {
    return (
      <Box>
        <Breadcrumb items={[
          { label: 'Channels', path: '/channels', icon: <ArticleIcon /> },
          { label: channel?.name || 'Channel', path: channel?._id ? `/channels/${channel._id}` : '/channels', icon: <ArticleIcon /> },
          { label: show?.name || 'Show', path: show?._id ? `/shows/${show._id}` : '/channels', icon: <ArticleIcon /> },
          { label: episode?.title || 'Episode', path: `/episodes/${episodeId}`, icon: <ArticleIcon /> },
          { label: 'Segment Not Found', path: `/episodes/${episodeId}/segments/${segmentId}`, icon: <ArticleIcon /> }
        ]} />
        <Alert severity="error" sx={{ m: 2 }}>Segment not found</Alert>
      </Box>
    );
  }

  return (
    <Box className="fade-in">
      <Breadcrumb items={[
        { label: 'Channels', path: '/channels', icon: <ArticleIcon /> },
        { label: channel?.name || 'Channel', path: channel?._id ? `/channels/${channel._id}` : '/channels', icon: <ArticleIcon /> },
        { label: show?.name || 'Show', path: show?._id ? `/shows/${show._id}` : '/channels', icon: <ArticleIcon /> },
        { label: episode?.title || 'Episode', path: `/episodes/${episodeId}`, icon: <ArticleIcon /> },
        { label: 'Segment Management', path: `/episodes/${episodeId}/segments/${segmentId}`, icon: <ArticleIcon /> }
      ]} />
      
      <Box sx={{ p: 2 }}>

        {/* Global Operation Status */}
        {(operationStates.addingNewsItem || operationStates.generatingScript || operationStates.generatingAudio || operationStates.deletingNewsItem || operationStates.reorderingNewsItems) && (
          <Card sx={{ mb: 2, bgcolor: 'info.main', color: 'white' }}>
            <CardContent sx={{ py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LinearProgress sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { bgcolor: 'white' } }} />
                <Typography variant="body2">
                  {operationStates.addingNewsItem && 'Adding news item...'}
                  {operationStates.generatingScript && 'Generating script...'}
                  {operationStates.generatingAudio && 'Generating audio...'}
                  {operationStates.deletingNewsItem && 'Removing news item...'}
                  {operationStates.reorderingNewsItems && 'Reordering news items...'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Global Audio Status */}
        {audioState.isPlaying && (
          <Card sx={{ mb: 2, bgcolor: 'primary.main', color: 'white' }}>
            <CardContent sx={{ py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VolumeUpIcon />
                  <Typography variant="body2">
                    {audioState.currentPartIndex !== null 
                      ? `Playing part ${audioState.currentPartIndex + 1} of segment`
                      : 'Playing segment audio'
                    }
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<StopIcon />}
                  onClick={handleStopAudio}
                  sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
                >
                  Stop
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography 
                variant="h5" 
                component="h1"
                sx={{ 
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em',
                }}
              >
                {segment.title}
              </Typography>
              <IconButton
                size="small"
                onClick={handleOpenEditSegmentDialog}
                sx={{
                  color: 'grey.600',
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: 'primary.50',
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
              {segment.description}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip 
                label={segment.status} 
                color={getStatusColor(segment.status) as any}
                size="small"
                sx={{ fontWeight: 500 }}
              />
              <Chip 
                label={`${newsItems.length} news items`}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontWeight: 500 }}
              />
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAddNewsDialog(true)}
            disabled={operationStates.addingNewsItem || operationStates.generatingScript || operationStates.generatingAudio}
            size="small"
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              borderRadius: 1.5,
              px: 2,
              py: 1,
              boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.4)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            {operationStates.addingNewsItem ? 'Adding...' : 'Add News Item'}
          </Button>
        </Box>

        {/* News Items Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Current News Items
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Drag and drop news items to reorder them within this segment
            </Typography>

            {newsItems.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <ArticleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No news items in this segment yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Add news items to get started with content creation
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setShowAddNewsDialog(true)}
                >
                  Add First News Item
                </Button>
              </Box>
            ) : (
              <List sx={{ bgcolor: 'background.paper' }}>
                {newsItems.map((item, index) => (
                  <ListItem
                    key={item._id}
                    draggable={!operationStates.reorderingNewsItems && !operationStates.addingNewsItem && !operationStates.generatingScript && !operationStates.generatingAudio && !operationStates.deletingNewsItem}
                    onDragStart={(e) => handleDragStart(e, item._id)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, item._id)}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      bgcolor: draggedItem === item._id ? 'action.hover' : 'background.paper',
                      '&:hover': { bgcolor: 'action.hover' },
                      cursor: (operationStates.reorderingNewsItems || operationStates.addingNewsItem || operationStates.generatingScript || operationStates.generatingAudio || operationStates.deletingNewsItem) ? 'not-allowed' : 'grab',
                      '&:active': { cursor: 'grabbing' },
                      opacity: (operationStates.reorderingNewsItems || operationStates.addingNewsItem || operationStates.generatingScript || operationStates.generatingAudio || operationStates.deletingNewsItem) ? 0.6 : 1,
                    }}
                  >
                    <DragIcon 
                      sx={{ mr: 2, color: 'text.secondary' }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {item.article?.title || 'Unknown Article'}
                        </Typography>
                        <Chip 
                          label={`Order: ${item.order}`}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {item.article?.description || 'No description available'}
                      </Typography>
                      
                      {/* Full Contents Section */}
                      {item.article?.fullContents && (
                        <Box sx={{ mb: 1 }}>
                          <Button
                            size="small"
                            startIcon={expandedNewsItems.has(item._id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            onClick={() => toggleNewsItemExpansion(item._id)}
                            sx={{ 
                              textTransform: 'none',
                              color: 'primary.main',
                              '&:hover': { backgroundColor: 'primary.50' }
                            }}
                          >
                            {expandedNewsItems.has(item._id) ? 'Hide Full Content' : 'Show Full Content'}
                          </Button>
                          
                          {expandedNewsItems.has(item._id) && (
                            <Box sx={{ 
                              mt: 1, 
                              p: 2, 
                              bgcolor: 'grey.50', 
                              borderRadius: 1, 
                              border: 1, 
                              borderColor: 'grey.200',
                              maxHeight: '300px',
                              overflow: 'auto'
                            }}>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                {item.article.fullContents}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                      
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip 
                          label={item.article?.source?.name || 'Unknown Source'}
                          size="small"
                          variant="outlined"
                        />
                        {item.article?.publishedAt && (
                          <Typography variant="caption" color="text.secondary">
                            {new Date(item.article.publishedAt).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Article">
                          <IconButton
                            size="small"
                            onClick={() => window.open(item.article?.url, '_blank')}
                            disabled={!item.article?.url}
                          >
                            <OpenInNewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove from Segment">
                          <IconButton
                            size="small"
                            color="error"
                            disabled={operationStates.deletingNewsItem || operationStates.generatingScript || operationStates.generatingAudio || operationStates.addingNewsItem}
                            onClick={() => handleDeleteNewsItem(item._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>

        {/* Segment Actions */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Segment Actions
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<MicIcon />}
                  disabled={newsItems.length === 0 || operationStates.generatingScript || operationStates.generatingAudio || operationStates.addingNewsItem}
                  onClick={handleGenerateScript}
                >
                  {operationStates.generatingScript ? 'Generating...' : 'Generate Script'}
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AudiotrackIcon />}
                  disabled={(!segment.script && !segment.scriptParts?.length) || operationStates.generatingAudio || operationStates.generatingScript || operationStates.addingNewsItem}
                  onClick={handleGenerateAudio}
                >
                  {operationStates.generatingAudio ? 'Generating...' : 'Generate Audio'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Script and Audio Status */}
        {(segment.script || segment.scriptParts?.length > 0 || segment.audioGenerationStatus) && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Generated Content
              </Typography>
              
              {/* Script Section */}
              {segment.script && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Script
                  </Typography>
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2">
                      {segment.script}
                    </Typography>
                  </Box>
                </Box>
              )}
              
              {/* Script Parts Section */}
              {segment.scriptParts && segment.scriptParts.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1">
                      Script Parts
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={audioState.isPlaying && audioState.currentSegmentId === segment._id ? <StopIcon /> : <PlayArrowIcon />}
                        onClick={() => handlePlayAudioForSegment(segment)}
                        disabled={!segment.scriptParts.some(part => part.audioUrl)}
                      >
                        {audioState.isPlaying && audioState.currentSegmentId === segment._id ? 'Stop All' : 'Play All'}
                      </Button>
                      {audioState.isPlaying && audioState.currentSegmentId === segment._id && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<StopIcon />}
                          onClick={handleStopAudio}
                        >
                          Stop
                        </Button>
                      )}
                    </Box>
                  </Box>
                  {segment.scriptParts.map((part, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {part.panelistName}:
                        </Typography>
                        {part.audioUrl && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={
                              audioState.isPlaying && 
                              audioState.currentSegmentId === segment._id && 
                              audioState.currentPartIndex === part.order 
                                ? <StopIcon /> 
                                : <PlayArrowIcon />
                            }
                            onClick={() => handlePlayAudioForSegment(segment, part.order)}
                          >
                            {audioState.isPlaying && 
                             audioState.currentSegmentId === segment._id && 
                             audioState.currentPartIndex === part.order 
                              ? 'Stop' 
                              : 'Play'}
                          </Button>
                        )}
                      </Box>
                      <Typography variant="body2">
                        {part.script}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              
              {/* Audio Status Section */}
              {segment.audioGenerationStatus && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Audio Status
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip 
                      label={segment.audioGenerationStatus}
                      color={
                        segment.audioGenerationStatus === 'completed' ? 'success' :
                        segment.audioGenerationStatus === 'failed' ? 'error' :
                        segment.audioGenerationStatus === 'generating' ? 'warning' : 'default'
                      }
                    />
                    {segment.audioDuration && (
                      <Typography variant="body2" color="text.secondary">
                        Duration: {Math.round(segment.audioDuration)}s
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Add News Dialog */}
      <Dialog 
        open={showAddNewsDialog} 
        onClose={handleCloseAddNewsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add News Item to Segment</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Search for news articles"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Enter keywords, title, or URL to search..."
              sx={{ mb: 2 }}
            />
            
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearchNews}
                disabled={!searchQuery.trim() || searchLoading}
              >
                {searchLoading ? 'Searching...' : 'Search'}
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                size="small"
              >
                {showAdvancedFilters ? 'Hide Filters' : 'Advanced Filters'}
              </Button>
            </Box>

            {showAdvancedFilters && (
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Advanced Search Filters
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Language"
                      value={searchFilters.language}
                      onChange={(e) => setSearchFilters(prev => ({ ...prev, language: e.target.value }))}
                      placeholder="en, es, fr, etc."
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Categories"
                      value={searchFilters.categories}
                      onChange={(e) => setSearchFilters(prev => ({ ...prev, categories: e.target.value }))}
                      placeholder="technology,science,business"
                      size="small"
                      helperText="Comma-separated categories"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Published After"
                      type="date"
                      value={searchFilters.publishedAfter}
                      onChange={(e) => setSearchFilters(prev => ({ ...prev, publishedAfter: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Published Before"
                      type="date"
                      value={searchFilters.publishedBefore}
                      onChange={(e) => setSearchFilters(prev => ({ ...prev, publishedBefore: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Limit"
                      type="number"
                      value={searchFilters.limit}
                      onChange={(e) => setSearchFilters(prev => ({ ...prev, limit: parseInt(e.target.value) || 20 }))}
                      inputProps={{ min: 1, max: 50 }}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Paper>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {searchLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <LinearProgress sx={{ flex: 1 }} />
                <Typography variant="body2">Searching...</Typography>
              </Box>
            )}

            {searchResults.length > 0 ? (
              <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                {searchResults.map((article) => (
                  <Card key={article._id} sx={{ mb: 2, p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          {article.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {article.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip 
                            label={article.source?.name || 'Unknown Source'} 
                            size="small" 
                            variant="outlined"
                          />
                          <Typography variant="caption" color="text.secondary">
                            {article.publishedAt && new Date(article.publishedAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleAddNewsToSegment(article)}
                        disabled={operationStates.addingNewsItem}
                        sx={{ ml: 2 }}
                      >
                        {operationStates.addingNewsItem ? 'Adding...' : 'Add to Segment'}
                      </Button>
                    </Box>
                  </Card>
                ))}
              </Box>
            ) : searchQuery && !searchLoading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  No results found for "{searchQuery}"
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Try adjusting your search terms or filters
                </Typography>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography color="text.secondary">
                  Search for news articles to add to this segment
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Enter search terms above to find relevant news
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddNewsDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Script Generation Dialog */}
      <Dialog 
        open={showScriptGenerationDialog} 
        onClose={() => setShowScriptGenerationDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generate Script</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* AI Provider Selection */}
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">AI Provider</FormLabel>
              <RadioGroup
                value={selectedProvider}
                onChange={(e) => setSelectedProvider(e.target.value as 'openai' | 'google')}
                row
              >
                <FormControlLabel 
                  value="openai" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography variant="body1">OpenAI (GPT-4o)</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Faster response times, higher cost
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel 
                  value="google" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography variant="body1">Google (Gemini)</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Good performance, lower cost
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
              <FormHelperText>
                Choose the AI provider for script generation. OpenAI is the default and recommended option.
              </FormHelperText>
            </FormControl>

            <Divider sx={{ my: 2 }} />

            {/* Script Style */}
            <TextField
              fullWidth
              label="Script Style"
              value={scriptStyle}
              onChange={(e) => setScriptStyle(e.target.value)}
              placeholder="e.g., conversational, formal, engaging, analytical"
              sx={{ mb: 2 }}
              helperText="Describe the tone and style you want for the generated script"
            />

            {/* Custom Prompt */}
            <TextField
              fullWidth
              label="Custom Prompt (Optional)"
              value={scriptPrompt}
              onChange={(e) => setScriptPrompt(e.target.value)}
              placeholder="Add any specific instructions for the script generation..."
              multiline
              rows={3}
              sx={{ mb: 2 }}
              helperText="Optional: Provide specific instructions or context for the AI to consider when generating the script"
            />

            {/* Information Box */}
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Script Generation Process:</strong>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                • The AI will analyze the news items in this segment
              </Typography>
              <Typography variant="body2">
                • Generate a discussion script based on the selected style and prompt
              </Typography>
              <Typography variant="body2">
                • Create individual parts for each panelist assigned to the show
              </Typography>
              <Typography variant="body2">
                • Processing typically takes 30-60 seconds
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowScriptGenerationDialog(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleScriptGenerationSubmit}
            disabled={operationStates.generatingScript}
            startIcon={<MicIcon />}
          >
            {operationStates.generatingScript ? 'Generating...' : `Generate Script with ${selectedProvider === 'openai' ? 'OpenAI' : 'Google'}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Segment Dialog */}
      <Dialog 
        open={showEditSegmentDialog} 
        onClose={handleCloseEditSegmentDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Segment</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Segment Title"
              value={editSegmentData.title}
              onChange={(e) => setEditSegmentData({ ...editSegmentData, title: e.target.value })}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={editSegmentData.description}
              onChange={(e) => setEditSegmentData({ ...editSegmentData, description: e.target.value })}
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditSegmentDialog}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleUpdateSegment}
            startIcon={<EditIcon />}
          >
            Update Segment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Message Snackbar */}
      <Alert
        severity="success"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 2000,
          display: showSuccessMessage ? 'block' : 'none',
        }}
        onClose={() => setShowSuccessMessage(false)}
      >
        {successMessage}
      </Alert>
    </Box>
  );
};

export default SegmentManagement;
